/*
 * Sensor/occupancy dashboard, used by deployments that set REACT_APP_DASHBOARD=ghd, where
 * it is what /user lands on in place of the alarm viewer.
 *
 * Ported from the ghd-poc branch, where it was src/dashboard-ghd.js wired directly into the
 * router. Here it is a variant the admin landing route selects at runtime, so one image still
 * serves every client - see src/admin/dashboard.js for the alarm-oriented default.
 *
 * It reads GET /api/v1/data/sensors/schedule, which esb-data already serves: SensorController
 * and its repositories arrived on trunk with the nsp lineage, which descends from ghd-poc.
 * Nothing backend-side had to be added for this screen.
 */
import React from "react";
import { Box, Button, Container, Divider, FormControlLabel, MenuItem, TextField, Typography, Checkbox, LinearProgress, Paper } from "@mui/material";
import { CustomDataGrid } from "../components/CustomDataGrid";
import { getScheduleTotals } from "../libs/apiCalls";

import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import ApplicationContext from '../contexts/application';
import { DatePicker } from "@mui/x-date-pickers";
import { GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid";
import { BarChart, axisClasses } from "@mui/x-charts";

function DashboardGhd() {
    const theme = useTheme();

    dayjs.extend(utc);
    dayjs.extend(timezone);

    const { settings } = React.useContext(ApplicationContext);

    const [gridRows, setGridRows] = React.useState([]);
    const [selectedDate, setSelectedDate] = React.useState(dayjs().format('YYYY-MM-DD'));
    const [selectedType, setSelectedType] = React.useState('WEEKDAY');
    const [forceRebuild, setForceRebuild] = React.useState(selectedDate === dayjs().format('YYYY-MM-DD'));
    const [loading, setLoading] = React.useState(false);
    const [isFixed, setIsFixed] = React.useState(false);
    
    const columns = [
        {
            field: 'SailDate', headerName: isFixed ? 'Depart Time' : 'Doors Closed',
            flex: 0.15,
            valueFormatter: (date) => dayjs(date).tz('America/Toronto').format('MM/DD HH:mm')
        },
        {
            field: 'Adult', headerName: 'Adults',
            // description: 'Includes Wheelchairs',
            type: 'number',
            align: 'right',
            flex: 0.1
        },
        {
            field: 'Child', headerName: 'Children',
            description: 'Includes Strollers',
            type: 'number',
            align: 'right',
            flex: 0.1
        },
        {
            field: 'Boardings', headerName: 'Boardings',
            description: 'Adults + Children',
            type: 'number',
            align: 'right',
            flex: 0.1,
            cellClassName: 'cell-total',
        },
        {
            field: 'Bicycle', headerName: 'Bikes',
            type: 'number',
            align: 'right',
            flex: 0.1
        },
        {
            field: 'Wheelchair', headerName: 'Wheelchairs',
            type: 'number',
            align: 'right',
            flex: 0.1
        },
        {
            field: 'Pram', headerName: 'Strollers',
            type: 'number',
            align: 'right',
            flex: 0.1
        },
        {
            field: 'Total', headerName: 'Total *',
            description: 'Boardings + Bikes',
            type: 'number',
            align: 'right',
            flex: 0.1,
            cellClassName: 'cell-total',
            renderCell: (props) => {
                return (
                    <strong>{props.row.Total}</strong>
                );
            }
        }
    ];

    const getTotals = (date, type, fixed, force) => {
        setLoading(true);
        getScheduleTotals(settings.token, date, type, fixed, force,
            (response) => {
                setLoading(false);
                setGridRows(JSON.parse(response.data.result));
            },
            (error) => {
                setLoading(false);
                console.log(error.message);
            }
        );
    };

    const handleScheduleChange = (date) => {
        const dateValue = date ? dayjs(date.$d).format('YYYY-MM-DD') : '';
        setSelectedDate(dateValue);

        const time = '05:00'; 
        const current = dayjs().format('YYYY-MM-DD HH:mm');
        const startRange = dayjs(date.$d).format('YYYY-MM-DD') + 'T' + time;
        const endRange = dayjs(date.$d).add(1, 'days').format('YYYY-MM-DD') + 'T' + time;

        setForceRebuild(startRange <= current && endRange >= current);
    };
    
    const handleTypeChange = (event) => {
        setSelectedType(event.target.value);
    };

    const handleSearchClick = () => {
        if (selectedDate !== '' && selectedType !== '') {
            getTotals(selectedDate, selectedType, isFixed ? '1' : '0', forceRebuild ? '1' : '0');
        };
    };

    const handleFixedScheduleChange = (event) => {
        setIsFixed(event.target.checked);
        getTotals(selectedDate, selectedType, event.target.checked, forceRebuild);
    };

    React.useEffect(() => {
        const dayOfWeek = dayjs(selectedDate).day();
        if (dayOfWeek === 0) {
            setSelectedType('SUN');
        }
        else if (dayOfWeek === 6) {
            setSelectedType('SAT');
        }
        else {
            setSelectedType('WEEKDAY');
        }
    }, [selectedDate]);

    React.useEffect(() => {
        if (Object.keys(settings).length > 0) {
            getTotals(selectedDate, selectedType, isFixed, forceRebuild);
        }
    }, []);



    return (
        <>
            <Box sx={{ display: 'flex', mb: 2 }}>
                <Box sx={{ width: "100%" }}>
                    <Typography sx={{ marginLeft: "5px", fontWeight: '400', color: 'text.bright' }} style={{ fontSize: "25px" }}>Passenger Counts</Typography>
                    <Typography sx={{ marginLeft: "5px", fontWeight: '300', color: 'text.secondary' }} style={{ fontSize: "15px" }}>View passenger counts for fixed and automatic schedules.</Typography>
                </Box>
            </Box>
            <Divider sx={{ mb: 4 }} />
            <Container maxWidth='md' sx={{ minHeight: '250px' }}>
                <Box sx={{ display: 'flex', mb: 4}}>
                    <BarChart
                            dataset={gridRows.length > 0 ? gridRows : [{}]}
                            margin={{
                                top: 16,
                                left: 70,
                                bottom: 55,
                            }}
                            height={200}
                            xAxis={[
                                {
                                    scaleType: 'band',
                                    dataKey: 'SailDate',
                                    label: isFixed ? 'Depart Time' : 'Doors Closed',
                                    tickLabelStyle: theme.typography.body2,
                                    valueFormatter: (value) => dayjs(value).tz('America/Toronto').format('HH:mm'),
                                },
                            ]}
                            yAxis={[
                                {
                                    label: 'Total',
                                    labelStyle: {
                                        ...theme.typography.body1,
                                        fill: theme.palette.text.primary,
                                    },
                                    tickLabelStyle: theme.typography.body2,
                                    tickNumber: 3,
                                },
                            ]}
                            series={[
                                { label: 'Adults', data: gridRows.map(r => r.Adult), stack: 'total' },
                                { label: 'Children', data: gridRows.map(r => r.Child), stack: 'total' },
                                { label: 'Bicycles', data: gridRows.map(r => r.Bicycle), stack: 'total' },
                                { label: 'Wheelchairs', data: gridRows.map(r => r.Wheelchair), stack: 'total' },
                                { label: 'Strollers', data: gridRows.map(r => r.Pram), stack: 'total' },

                            ]}
                            slotProps={{ legend: { hidden: true } }}
                            sx={{
                                [`.${axisClasses.root} line`]: { stroke: theme.palette.text.secondary },
                                [`.${axisClasses.root} text`]: { fill: theme.palette.text.secondary },
                                [`& .${axisClasses.left} .${axisClasses.label}`]: {
                                    transform: 'translateX(-25px)',
                                },

                                display: gridRows.length > 0 ? 'block' : 'none'
                            }}

                    />
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ display: 'flex', margin: 'auto' }}>
                    <DatePicker
                        label='Schedule Date'
                        name='scheduleDate'
                        slotProps={{
                            textField: {
                                size: 'small',
                                placeholder: 'Select Date'
                            }
                        }}
                        sx={{
                            width: '200px',
                            mb: 2
                        }}
                        onChange={(date) => handleScheduleChange(date)}
                        value={selectedDate !== '' ? dayjs(selectedDate) : null}
                    />

                    <TextField
                        label='Schedule Type'
                        name='type'
                        size='small'
                        select
                        sx={{
                            ml: 2,
                            width: '200px',
                            '& .MuiSelect-select span::before': {
                                content: "'Type'",
                            },
                        }}
                        value={selectedType}
                        onChange={handleTypeChange}
                        disabled
                    >
                        <MenuItem value='WEEKDAY'>Weekday</MenuItem>
                        <MenuItem value='SAT'>Saturday</MenuItem>
                        <MenuItem value='SUN'>Sunday</MenuItem>
                    </TextField>

                    <FormControlLabel
                        label='Use Fixed Schedule'
                        control={
                            <Checkbox
                                color='primary'
                                checked={isFixed}
                                onChange={handleFixedScheduleChange}
                            />
                        }
                        sx={{ ml: 2, mb: 2, width: '250px' }}
                    />

                    <Button variant="contained" color="primary"
                        style={{ width: "120px", height: "40px", marginLeft: 190 }}
                        onClick={handleSearchClick}
                        disableElevation
                    >
                        Search
                    </Button>         

                </Box>
                <Box sx={{ display: 'flex' }}>
                    <LinearProgress sx={{ width: '100%', display: loading ? 'block' : 'none' }} />
                </Box>
                <Box sx={{ display: 'flex' }}>
                    <CustomDataGrid
                        columns={columns}
                        rows={gridRows}
                        getRowId={(row) => row.SailDate}

                        pagination
                        pageSizeOptions={[20, 40, 60, 80, 100]}

                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 100,
                                },
                            }
                        }}

                        getRowClassName={(params) =>
                            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                        }

                        slots={{
                            toolbar: () => {
                                return (
                                    <GridToolbarContainer>
                                        <GridToolbarExport />
                                    </GridToolbarContainer>
                                )
                            }
                        }}

                        autoHeight
                    />

                </Box>
            </Container>
        </>
    )
}

export default DashboardGhd;